import { UseControllerProps } from 'react-hook-form/dist/types/controller';
import {
  CANADA_REGION_CODES,
  EQUIPMENT_CONDITION,
  FINANCING_TYPE,
  LEGAL_STRUCTURE,
  US_REGION_CODES,
  FinanceProgramType,
  PaymentPeriodTiming,
  FinanceProgramRateType,
  SubsidyCapType,
  MarkupType,
} from '@trnsact/trnsact-shared-types';
import { CriteriaValue } from 'modules/jsonRulesCriterias/businessCriteria/types';
import { convertFactToSelectOptions } from 'modules/jsonRulesCriterias/businessCriteria';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { FACT_NAMES_BOOLEAN, FACT_NAMES_NUMERIC, FACT_NAMES_STRING } from '@trnsact/business-criteria';
import { FinanceProgramForm, FinanceProgramTierForm } from '../schema';
import {
  AllRules,
  FinanceProgramFormSteps,
  FinanceProgramTiersTableContext,
  InsuranceLabelsByKey,
  MarkupMethods,
  Structures,
} from '../types';
import { Column } from 'components/shared/TableListing';
import { Stack, Box, Typography } from '@mui/material';
import { CurrencyInputField, InputField, InterestInputField, MultiSelectWithCustomOptions } from 'components/form';

export const rateTypeOptions = [
  { label: 'Interest Rate', value: FinanceProgramRateType.InterestRate },
  { label: 'Rate Factor', value: FinanceProgramRateType.RateFactor },
];

export const structureOptions = [
  { label: 'Markup', value: Structures.Markup },
  { label: 'Subsidy', value: Structures.Subsidy },
  { label: 'None', value: Structures.None },
];

export const markupOptions = [
  { label: 'Markup %', value: MarkupMethods.Markup },
  { label: 'Customer Rate', value: MarkupMethods.CustomerRate },
];

export const programTypeOptions = [
  { label: 'Loan', value: FinanceProgramType.Loan },
  { label: 'Lease', value: FinanceProgramType.Lease },
  { label: 'EFA', value: FinanceProgramType.Efa },
];

export const paymentCalculationMethodOptions = [
  { label: 'Advance', value: PaymentPeriodTiming.BeginningOfPeriod },
  { label: 'Arrears', value: PaymentPeriodTiming.EndOfPeriod },
];

export const termsOptions = [
  { label: '12', value: '12' },
  { label: '24', value: '24' },
  { label: '36', value: '36' },
  { label: '48', value: '48' },
  { label: '60', value: '60' },
  { label: '72', value: '72' },
  { label: '84', value: '84' },
  { label: '96', value: '96' },
  { label: '108', value: '108' },
  { label: '120', value: '120' },
];

export const booleanOptions = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

export const capTypeOptions = [
  { label: 'Dealer Net Invoice %', value: SubsidyCapType.NetInvoicePercentage },
  { label: 'Fixed $ Amount', value: SubsidyCapType.FixedAmount },
];

export const getInitialCriteriaConditionFormValue = (fact: AllRules): CriteriaValue => ({
  fact,
  number: { min: null, max: null },
  boolean: { value: false },
  string: { value: [] },
  isRequiredToQuote: false,
});

export const factNamesNumeric = convertFactToSelectOptions(FACT_NAMES_NUMERIC);
export const factNamesBoolean = convertFactToSelectOptions(FACT_NAMES_BOOLEAN);
export const factNamesString = convertFactToSelectOptions(FACT_NAMES_STRING);

export const allFacts = [...factNamesNumeric, ...factNamesString, ...factNamesBoolean];

export const triggerPathByStep: Record<FinanceProgramFormSteps, UseControllerProps<FinanceProgramForm>['name']> = {
  [FinanceProgramFormSteps.First]: 'program.first',
  [FinanceProgramFormSteps.Second]: 'program.second',
  [FinanceProgramFormSteps.Third]: 'program.third',
  [FinanceProgramFormSteps.Fourth]: 'program.fourth',
  [FinanceProgramFormSteps.Fifth]: 'program.fifth',
  [FinanceProgramFormSteps.Sixth]: 'program.sixth',
};

const equipmentConditionOptions = convertEnumToSelectOptions(EQUIPMENT_CONDITION);
const financingTypeOptions = convertEnumToSelectOptions(FINANCING_TYPE, { useKeyAsValue: true });
const legalStructureOptions = convertEnumToSelectOptions(LEGAL_STRUCTURE);
const canadaRegionOptions = convertEnumToSelectOptions(CANADA_REGION_CODES);
const usRegionOptions = convertEnumToSelectOptions(US_REGION_CODES);

export const allStringFactsOptions = [
  ...equipmentConditionOptions,
  ...financingTypeOptions,
  ...legalStructureOptions,
  ...canadaRegionOptions,
  ...usRegionOptions,
];

export const dialogTitleByFormStep: Record<FinanceProgramFormSteps, string> = {
  [FinanceProgramFormSteps.First]: 'Program Details',
  [FinanceProgramFormSteps.Second]: 'Configure Structure',
  [FinanceProgramFormSteps.Third]: 'Choose Criteria (Optional)',
  [FinanceProgramFormSteps.Fourth]: 'Submission Settings',
  [FinanceProgramFormSteps.Fifth]: 'Insurance Coverage Requirements',
  [FinanceProgramFormSteps.Sixth]: 'Create Finance Program?',
};

export const insuranceLabelsByKey: InsuranceLabelsByKey = {
  coverageRequirements: 'Coverage Requirements',
  certificateRequirements: 'Certificate Requirements',
  otherInstructions: 'Other Instructions',
};

export const feesMarkupTypesOptions = [
  { label: '$', value: MarkupType.Flat },
  { label: '%', value: MarkupType.Percentage },
];

export const financeProgramTiersColumns: Column<FinanceProgramTierForm, FinanceProgramTiersTableContext>[] = [
  {
    renderKey: 'name',
    header: 'Tier Name',
    renderCell: ({ context: { form }, rowIndex }) => (
      <Stack direction="row" gap="1rem" width="100%" alignItems="center">
        <Box
          sx={{
            backgroundColor: form.getValues(`bodyFields.${rowIndex}.color`),
            width: '20px',
            height: '20px',
            borderRadius: '50%',
          }}
        ></Box>
        <Typography>{form.getValues(`bodyFields.${rowIndex}.name`)}</Typography>
      </Stack>
    ),
    width: '250px',
  },
  {
    renderKey: 'maxApprovalAmount',
    header: 'Max Approval Amount',
    renderCell: ({ context: { form }, rowIndex }) => (
      <CurrencyInputField
        control={form.control}
        name={`bodyFields.${rowIndex}.maxApprovalAmount`}
        textFieldProps={{ variant: 'outlined', placeholder: 'Max Approval Amount' }}
      />
    ),
    width: '250px',
  },
  {
    renderKey: 'terms',
    header: 'Term Options',
    renderCell: ({ context: { form }, rowIndex }) => (
      <MultiSelectWithCustomOptions
        isOptionsSorted
        control={form.control}
        options={termsOptions}
        name={`bodyFields.${rowIndex}.terms`}
        onChange={(selected: (string | number)[]) =>
          form.setValue(`bodyFields.${rowIndex}.terms`, selected.map(String))
        }
        selectProps={{ label: 'Terms in Months', variant: 'outlined', size: 'small' }}
        inputProps={{ type: 'number', label: 'Abb custom term', placeholder: 'Enter your own term' }}
      />
    ),
    width: '250px',
  },
  {
    renderKey: 'interestRate',
    header: 'Interest Rate',
    renderCell: ({ context: { form }, rowIndex }) => (
      <InterestInputField
        control={form.control}
        name={`bodyFields.${rowIndex}.interestRate`}
        textFieldProps={{ variant: 'outlined', placeholder: 'Interest Rate' }}
      />
    ),
    width: '250px',
  },
  /* {
    renderKey: 'remainingLifePercentage',
    header: '% of Remaining Life',
    renderCell: ({ context: { form }, rowIndex }) => (
      <InterestInputField
        control={form.control}
        name={`bodyFields.${rowIndex}.remainingLifePercentage`}
        textFieldProps={{ variant: 'outlined', placeholder: '% of Remaining Life' }}
      />
    ),
    width: '200px',
  },
  {
    renderKey: 'tierMaxTerm',
    header: 'Tier Max Term',
    renderCell: ({ context: { form }, rowIndex }) => (
      <InputField
        control={form.control}
        label="Tier Max Term"
        name={`bodyFields.${rowIndex}.tierMaxTerm`}
        inputProps={{ type: 'number', placeholder: 'Tier Max Term', variant: 'outlined' }}
      />
    ),
    width: '200px',
  },
  {
    renderKey: 'maxTerm',
    header: 'Max Term',
    renderCell: ({ context: { form }, rowIndex }) => (
      <InputField
        control={form.control}
        label="Max Term"
        name={`bodyFields.${rowIndex}.maxTerm`}
        inputProps={{ type: 'number', placeholder: 'Max Term', variant: 'outlined' }}
      />
    ),
    width: '200px',
  }, */
  {
    renderKey: 'suggestedDownPaymentPercentage',
    header: 'Suggested Down Payment',
    renderCell: ({ context: { form }, rowIndex }) => (
      <InterestInputField
        control={form.control}
        name={`bodyFields.${rowIndex}.suggestedDownPaymentPercentage`}
        textFieldProps={{ variant: 'outlined', placeholder: 'Suggested Down Payment' }}
      />
    ),
    width: '250px',
  },
  {
    renderKey: 'advancePayment',
    header: 'Advance Payment (# of)',
    renderCell: ({ context: { form }, rowIndex }) => (
      <InputField
        control={form.control}
        label="Advance Payment (# of)"
        name={`bodyFields.${rowIndex}.advancePayment`}
        inputProps={{ type: 'number', variant: 'outlined', placeholder: 'Advance Payment (# of)' }}
      />
    ),
    width: '250px',
  },
];

export const initialFinanceProgramTierTableData: Omit<FinanceProgramTierForm, 'name' | 'color'> = {
  maxApprovalAmount: null,
  interestRate: null,
  terms: [],
  remainingLifePercentage: null,
  tierMaxTerm: null,
  maxTerm: null,
  suggestedDownPaymentPercentage: null,
  advancePayment: null,
};
